<template>
  <div class="joke-container">
    <div class="crystal-ball">
      <div class="magic-sparkles"></div>

      <transition name="joke-flip" mode="out-in">
        <div v-if="jokeData" key="joke" class="joke-card">
          <div class="header">
            <span class="emoji">😂</span>
            <h2>{{ translatePunctuation(jokeData.data.title) }}</h2>
            <span class="emoji">💬</span>
          </div>
          <div class="content">
            <p class="joke-text">{{ translatePunctuation(jokeData.data.content) }}</p>
            <div class="interaction">
              <button
                @click="fetchNewJoke"
                :disabled="loading"
                class="refresh-btn"
              >
                <span v-if="!loading">🔄 再笑一次</span>
                <span v-else>🎭 憋个更大的笑点...</span>
              </button>
              <span class="id-tag">#{{ jokeData.data.id }}</span>
            </div>
          </div>
        </div>

        <div v-else-if="error" key="error" class="error-card">
          😿 笑料丢失！
          <p>{{ error }}</p>
          <button @click="fetchNewJoke">🎪 召唤新笑话</button>
        </div>

        <div v-else key="loading" class="loading-card">
          <div class="rotating-emoji">🎪</div>
          <p>马戏团小丑正在赶来...</p>
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
import axios from "axios";

const API_URL = "https://api.vvhan.com/api/text/joke?type=json";

const jokeData = ref(null);
const loading = ref(false);
const error = ref(null);
const translatePunctuation = (text) => {
  if (!text) return text;

  const punctuationMap = {
    "&rdquo;": "」",
    "&ldquo;": "「",
    "&rsquo;": "’",
    "&lsquo;": "‘",
    "&middot;": "·",
    "&hellip;": "…",
    "&mdash;": "—",
    "&ndash;": "–",
    "&quot;": '"',
    "&amp;": "&",
    "&lt;": "<",
    "&gt;": ">",
  };

  return text.replace(
    /&(rdquo|ldquo|rsquo|lsquo|middot|hellip|mdash|ndash|quot|amp|lt|gt);/g,
    (match) => punctuationMap[match] || match
  );
};

const fetchNewJoke = async () => {
  try {
    loading.value = true;
    error.value = null;

    const response = await axios.get(API_URL, {
      timeout: 5000,
    });

    if (response.data.success) {
      jokeData.value = response.data;
    } else {
      throw new Error("获取笑料失败");
    }
  } catch (err) {
    error.value =
      err.response?.data?.message || "笑话库存不足，请联系管理员补充";
    jokeData.value = null;
  } finally {
    loading.value = false;
  }
};

// 初始加载
fetchNewJoke();
</script>

<style scoped>
.joke-container {
  max-width: 800px;
  margin: 2rem auto;
  padding: 0 1rem;
}

.crystal-ball {
  position: relative;
  background: linear-gradient(145deg, #ffffff, #f8f9fa);
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  min-height: 300px;
}

.magic-sparkles {
  position: absolute;
  top: -10px;
  left: -10px;
  right: -10px;
  bottom: -10px;
  background: radial-gradient(
    circle at 50% 0%,
    rgba(255, 215, 0, 0.1) 0%,
    transparent 70%
  );
  pointer-events: none;
}

.joke-card {
  background: white;
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;

  h2 {
    color: #2d4059;
    font-size: 1.5rem;
    margin: 0;
  }
}

.joke-text {
  color: #6b778d;
  font-size: 1.1rem;
  line-height: 1.8;
  min-height: 120px;
}

.interaction {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 2rem;
}

.refresh-btn {
  background: linear-gradient(45deg, #ff7e5f, #feb47b);
  border: none;
  padding: 0.8rem 1.5rem;
  border-radius: 50px;
  color: white;
  font-weight: bold;
  cursor: pointer;
  transition: transform 0.3s;

  &:hover {
    transform: scale(1.05);
  }

  &:disabled {
    opacity: 0.7;
    cursor: wait;
  }
}

.id-tag {
  background: #f8f9fa;
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  color: #8795a7;
}

/* 加载状态样式 */
.loading-card {
  text-align: center;
  padding: 3rem;

  .rotating-emoji {
    font-size: 3rem;
    animation: spin 2s linear infinite;
  }

  p {
    color: #8395a7;
    margin-top: 1rem;
  }
}

.error-card {
  background: #ffe5e5;
  color: #ff6b6b;
  padding: 2rem;
  border-radius: 15px;
  text-align: center;

  button {
    background: #ff6b6b;
    color: white;
    border: none;
    padding: 0.8rem 1.5rem;
    border-radius: 50px;
    margin-top: 1rem;
  }
}

/* 动画 */
.joke-flip-enter-active,
.joke-flip-leave-active {
  transition: all 0.5s;
}

.joke-flip-enter-from,
.joke-flip-leave-to {
  opacity: 0;
  transform: rotateX(90deg);
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* 移动端适配 */
@media (max-width: 768px) {
  .crystal-ball {
    padding: 1.5rem;
  }

  .header {
    flex-direction: column;
    text-align: center;
    gap: 0.5rem;

    h2 {
      font-size: 1.3rem;
    }
  }

  .joke-text {
    font-size: 1rem;
    min-height: auto;
  }

  .interaction {
    flex-direction: column;
    gap: 1rem;

    button {
      width: 100%;
    }
  }

  .refresh-btn {
    padding: 0.8rem;
  }
}

@media (max-width: 480px) {
  .crystal-ball {
    border-radius: 15px;
  }

  .joke-card {
    padding: 1.2rem;
  }

  h2 {
    font-size: 1.2rem;
  }

  .id-tag {
    font-size: 0.9rem;
  }
}
</style>
